import {
  bindMiniAppCssVars,
  emitEvent,
  bindThemeParamsCssVars,
  bindViewportCssVars,
  expandViewport,
  init,
  isTMA,
  miniAppReady,
  mockTelegramEnv,
  mountBackButton,
  mountMainButton,
  mountSwipeBehavior,
  mountMiniAppSync,
  mountThemeParamsSync,
  mountViewport,
  retrieveLaunchParams,
} from '@telegram-apps/sdk-react';

export interface TmaBootstrap {
  /** false — открыто вне Telegram в production: показываем экран «Откройте в Telegram». */
  available: boolean;
  /** true — окружение подменено для разработки вне Telegram. */
  mocked: boolean;
  platform: string;
  /** Deep link `startapp=…` (ref_, seat_, reserve_, pay_, g_) — обрабатывается с Stage 3. */
  startParam: string | null;
  /** Текст сбоя инициализации SDK: показывается на экране вместо пустоты. */
  error?: string;
}

const MOCK_THEME = {
  bg_color: '#141517',
  secondary_bg_color: '#1e2023',
  section_bg_color: '#26292e',
  section_separator_color: '#2f3339',
  text_color: '#f3f4f6',
  hint_color: '#8b8f98',
  link_color: '#f2a33c',
  button_color: '#f2a33c',
  button_text_color: '#141517',
  destructive_text_color: '#ef5350',
} as const;

/**
 * Светлая тема Telegram для проверки вне Telegram (Stage 17: бумага документа выбирается по `isDark`).
 * Включается адресом `?tgTheme=light` и держится до конца вкладки (`sessionStorage`).
 */
const MOCK_THEME_LIGHT = {
  bg_color: '#ffffff',
  secondary_bg_color: '#f1f1f4',
  section_bg_color: '#ffffff',
  section_separator_color: '#e2e3e8',
  text_color: '#000000',
  hint_color: '#8e8e93',
  link_color: '#2481cc',
  button_color: '#2481cc',
  button_text_color: '#ffffff',
  destructive_text_color: '#e53935',
} as const;

const MOCK_THEME_KEY = 'otryad.dev.tgTheme';

function mockTheme(): typeof MOCK_THEME | typeof MOCK_THEME_LIGHT {
  const fromUrl = new URLSearchParams(window.location.search).get('tgTheme');
  if (fromUrl === 'light' || fromUrl === 'dark') sessionStorage.setItem(MOCK_THEME_KEY, fromUrl);
  return sessionStorage.getItem(MOCK_THEME_KEY) === 'light' ? MOCK_THEME_LIGHT : MOCK_THEME;
}

const NO_INSETS = { left: 0, top: 0, right: 0, bottom: 0 };

/**
 * Подмена окружения в dev: пользователь и тема, чтобы приложение открывалось в обычном браузере.
 * Вне iframe Telegram `postEvent` бросает UnknownEnvError, поэтому запросы SDK (тема, viewport,
 * safe-area) отвечаем сами через `onEvent`, остальные методы (MainButton, хаптика) глотаем.
 */
function mockEnv(): void {
  const devTgId = Number(import.meta.env.VITE_DEV_TG_ID ?? 1);
  const user = {
    id: Number.isFinite(devTgId) && devTgId > 0 ? devTgId : 1,
    first_name: 'Dev',
    last_name: 'User',
    username: 'dev_user',
    language_code: 'ru',
    allows_write_to_pm: true,
  };
  const initData = new URLSearchParams([
    ['user', JSON.stringify(user)],
    ['auth_date', String(Math.floor(Date.now() / 1000))],
    ['hash', 'mock'],
    ['signature', 'mock'],
  ]);

  const theme = mockTheme();
  mockTelegramEnv({
    launchParams: {
      tgWebAppData: initData,
      tgWebAppVersion: '8',
      tgWebAppPlatform: 'tdesktop',
      tgWebAppThemeParams: theme,
    },
    onEvent([method, payload]) {
      switch (method) {
        case 'web_app_invoke_custom_method': {
          // CloudStorage и прочие custom methods вне Telegram недоступны — отвечаем ошибкой сразу,
          // чтобы промисы SDK не висели (черновик онбординга уходит в localStorage).
          const reqId = (payload as { req_id?: string } | undefined)?.req_id ?? '';
          return emitEvent('custom_method_invoked', { req_id: reqId, error: 'UNSUPPORTED' });
        }
        case 'web_app_request_theme':
          return emitEvent('theme_changed', { theme_params: theme });
        case 'web_app_request_viewport':
          return emitEvent('viewport_changed', {
            height: window.innerHeight,
            width: window.innerWidth,
            is_expanded: true,
            is_state_stable: true,
          });
        case 'web_app_request_safe_area':
          return emitEvent('safe_area_changed', NO_INSETS);
        case 'web_app_request_content_safe_area':
          return emitEvent('content_safe_area_changed', NO_INSETS);
        default:
          return undefined;
      }
    },
  });
}

/**
 * Инициализация Telegram Mini Apps SDK (BLUEPRINT 8, Stage 2):
 * themeParams → CSS-переменные `--tg-theme-*`, viewport → `--tg-viewport-*` (safe-area) + expand,
 * BackButton/MainButton монтируются здесь, управляются хуками в `TmaProvider`.
 */
/**
 * Есть ли хост Telegram, которому SDK может слать события: iframe (web-клиент),
 * `TelegramWebviewProxy` (мобильные и desktop), `external.notify` (macOS).
 * `isTMA()` недостаточно: после mock-запуска launch params остаются в sessionStorage,
 * и на перезагрузке `isTMA()` вернёт true без реального хоста — `init()` упадёт с UnknownEnvError.
 */
function hasTelegramHost(): boolean {
  const w = window as Window & {
    TelegramWebviewProxy?: unknown;
    external?: { notify?: unknown };
  };
  return (
    window.parent !== window ||
    typeof w.TelegramWebviewProxy !== 'undefined' ||
    typeof w.external?.notify === 'function'
  );
}

export function bootstrapTma(): TmaBootstrap {
  try {
    return start();
  } catch (err) {
    // Раньше сбой `init()` рушил модуль до первой отрисовки, и клиент показывал белый экран без
    // единого слова. Теперь приложение доходит до экрана и говорит, что именно не поднялось.
    const error = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
    console.error('TMA bootstrap failed', err);
    return { available: false, mocked: false, platform: 'unknown', startParam: null, error };
  }
}

function start(): TmaBootstrap {
  let mocked = false;

  if (!isTMA() || !hasTelegramHost()) {
    if (!import.meta.env.DEV) {
      return { available: false, mocked: false, platform: 'unknown', startParam: null };
    }
    mockEnv();
    mocked = true;
  }

  init();

  mountThemeParamsSync.ifAvailable();
  bindThemeParamsCssVars.ifAvailable();
  mountMiniAppSync.ifAvailable();
  bindMiniAppCssVars.ifAvailable();
  mountBackButton.ifAvailable();
  mountMainButton.ifAvailable();
  // Шторка на время жеста выключает вертикальные свайпы, иначе тяга вниз закрывает приложение
  mountSwipeBehavior.ifAvailable();

  if (mountViewport.isAvailable()) {
    mountViewport()
      .then(() => {
        expandViewport.ifAvailable();
        bindViewportCssVars.ifAvailable();
      })
      .catch(() => {
        /* viewport недоступен на старых клиентах — работаем без safe-area переменных */
      });
  }

  miniAppReady.ifAvailable();

  let platform = 'unknown';
  let startParam: string | null = null;
  try {
    const lp = retrieveLaunchParams();
    platform = lp.tgWebAppPlatform;
    startParam = lp.tgWebAppStartParam ?? null;
  } catch {
    /* параметры запуска недоступны — не критично */
  }

  return { available: true, mocked, platform, startParam };
}
